import { AlertTriangle, CheckCircle, XCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import type { DeliverabilityAccountHealth } from '@/types/api'

interface HealthBannerProps {
  accounts: DeliverabilityAccountHealth[]
  onReactivate: (accountId: string) => void
  isReactivating: boolean
}

export function HealthBanner({ accounts, onReactivate, isReactivating }: HealthBannerProps) {
  const unhealthyAccounts = accounts.filter((a) => a.status !== 'healthy')

  if (unhealthyAccounts.length === 0) return null

  return (
    <div className="space-y-2 mb-6">
      {unhealthyAccounts.map((account) => (
        <BannerItem
          key={account.email_account_id}
          account={account}
          onReactivate={onReactivate}
          isReactivating={isReactivating}
        />
      ))}
    </div>
  )
}

function BannerItem({
  account,
}: {
  account: DeliverabilityAccountHealth
  onReactivate: (accountId: string) => void
  isReactivating: boolean
}) {
  const { t } = useTranslation('common')

  if (account.status === 'paused') {
    return (
      <div className="flex items-center justify-between rounded-lg border border-red-200 bg-red-50 px-4 py-3">
        <div className="flex items-center gap-3">
          <XCircle className="h-5 w-5 text-red-600 shrink-0" />
          <div>
            <p className="font-medium text-red-800">
              {t('health.accountPaused', { email: account.email })}
            </p>
            <p className="text-sm text-red-600">
              {t('health.bounceRate', { rate: account.bounce_rate })}
              {account.complaint_rate > 0 &&
                ` · ${t('health.complaintRate', { rate: account.complaint_rate })}`}
            </p>
          </div>
        </div>
      </div>
    )
  }

  if (account.status === 'warning') {
    return (
      <div className="flex items-center justify-between rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3">
        <div className="flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-yellow-600 shrink-0" />
          <div>
            <p className="font-medium text-yellow-800">
              {t('health.accountWarning', { email: account.email, rate: account.bounce_rate })}
            </p>
            <p className="text-sm text-yellow-600">
              {t('health.autoPauseThreshold')}
            </p>
          </div>
        </div>
      </div>
    )
  }

  return null
}

export function RecoveryBanner({
  accounts,
  onReactivate,
  isReactivating,
}: HealthBannerProps) {
  const { t } = useTranslation('common')
  const recoveredAccounts = accounts.filter(
    (a) => a.status === 'healthy' && a.paused_at !== null
  )

  if (recoveredAccounts.length === 0) return null

  return (
    <div className="space-y-2 mb-6">
      {recoveredAccounts.map((account) => (
        <div
          key={account.email_account_id}
          className="flex items-center justify-between rounded-lg border border-green-200 bg-green-50 px-4 py-3"
        >
          <div className="flex items-center gap-3">
            <CheckCircle className="h-5 w-5 text-green-600 shrink-0" />
            <div>
              <p className="font-medium text-green-800">
                {t('health.accountRecovered', { email: account.email })}
              </p>
              <p className="text-sm text-green-600">
                {t('health.bounceRate', { rate: account.bounce_rate })}
              </p>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="border-green-300 text-green-700 hover:bg-green-100"
            onClick={() => onReactivate(account.email_account_id)}
            disabled={isReactivating}
          >
            {t('health.reactivate')}
          </Button>
        </div>
      ))}
    </div>
  )
}
